import React from 'react';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import { ArcBox, ArcButton, ArcGrid, ArcStack, ArcText } from '@src/core/ui';
import { BackupCodesDisplayProps } from './types';

// Shows the 10 one-time backup codes after enrollment / regeneration. These are displayed
// only once, so we give the user clear ways to keep them (copy + download).
const BackupCodesDisplay: React.FC<BackupCodesDisplayProps> = ({ codes }) => {
  const { t } = useTranslation();

  const onCopy = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success(t('mfa.backupCodes.copied'));
  };

  const onDownload = () => {
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = t('mfa.backupCodes.downloadFileName');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <ArcStack spacing={2}>
      <ArcBox
        sx={{
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ArcText fontSize={14} fontWeight={700} color='error.main'>
          {t('mfa.backupCodes.warning')}
        </ArcText>
      </ArcBox>

      <ArcGrid container spacing={1}>
        {codes.map((code) => (
          <ArcGrid size={6} key={code}>
            <ArcText
              fontSize={16}
              fontFamily='monospace'
              textAlign='center'
              sx={{ py: 0.5, bgcolor: 'background.default', borderRadius: 1 }}
            >
              {code}
            </ArcText>
          </ArcGrid>
        ))}
      </ArcGrid>

      <ArcStack direction='row' spacing={2}>
        <ArcButton.outlined startIcon={<ContentCopyIcon />} onClick={onCopy} sx={{ flex: 1 }}>
          {t('mfa.backupCodes.copy')}
        </ArcButton.outlined>
        <ArcButton.outlined startIcon={<FileDownloadIcon />} onClick={onDownload} sx={{ flex: 1 }}>
          {t('mfa.backupCodes.download')}
        </ArcButton.outlined>
      </ArcStack>
    </ArcStack>
  );
};

export default BackupCodesDisplay;
